import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, gbp, fmtDate, formatApiError, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Plus, Search, Trash2, Pencil, MapPin, ArrowRight, FileDown, Send, ChevronsUpDown, Check, Copy, Receipt, ChevronDown, ChevronRight } from "lucide-react";
import RepeatJobDialog from "@/components/RepeatJobDialog";
import CompleteJobDialog from "@/components/CompleteJobDialog";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "all",         label: "All statuses" },
  { value: "new",         label: "New" },
  { value: "assigned",    label: "Assigned" },
  { value: "in_progress", label: "In Progress" },
  { value: "complete",    label: "Complete" },
  { value: "on_hold",     label: "On Hold" },
  { value: "cancelled",   label: "Cancelled" },
];

const STATUS_STYLES = {
  new:         "bg-slate-100 text-slate-700 border-slate-200",
  assigned:    "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  complete:    "bg-emerald-50 text-emerald-700 border-emerald-200",
  on_hold:     "bg-orange-50 text-orange-700 border-orange-200",
  // legacy fallbacks
  pending:     "bg-slate-100 text-slate-700 border-slate-200",
  collected:   "bg-amber-50 text-amber-700 border-amber-200",
  delivered:   "bg-emerald-50 text-emerald-700 border-emerald-200",
  invoiced:    "bg-slate-900 text-white border-slate-900",
  cancelled:   "bg-red-50 text-red-700 border-red-200",
};

const STATUS_LABEL = {
  new: "New", assigned: "Assigned", in_progress: "In Progress",
  complete: "Complete", on_hold: "On Hold",
  pending: "Pending", collected: "Collected", delivered: "Delivered",
  invoiced: "Invoiced", cancelled: "Cancelled",
};

const PRIORITY_STYLES = {
  low: "bg-slate-50 text-slate-600 border-slate-200",
  normal: "bg-slate-100 text-slate-700 border-slate-200",
  high: "bg-amber-50 text-amber-700 border-amber-200",
  urgent: "bg-red-50 text-red-700 border-red-200",
};

export default function Jobs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/jobs").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => items.filter((j) => {
    if (statusFilter !== "all" && j.status !== statusFilter) return false;
    const s = q.toLowerCase().trim();
    if (!s) return true;
    if (s === "invoiced") return j.invoiced === true;
    if (s === "not invoiced" || s === "uninvoiced" || s === "unbilled") return j.invoiced !== true;
    return (
      j.number.toLowerCase().includes(s) ||
      (j.customer_snapshot?.name || "").toLowerCase().includes(s) ||
      (j.driver_snapshot?.name || "").toLowerCase().includes(s) ||
      (j.assignee_other || "").toLowerCase().includes(s) ||
      (j.collection_address || "").toLowerCase().includes(s) ||
      (j.dropoff_address || "").toLowerCase().includes(s) ||
      (j.po_number || "").toLowerCase().includes(s) ||
      (j.code || "").toLowerCase().includes(s)
    );
  }), [items, q, statusFilter]);

  const invoicedCount = filtered.filter((j) => j.invoiced === true).length;

  const [openGroups, setOpenGroups] = useState({});
  const visibleRows = useMemo(() => {
    const done = (j) => j.status === "invoiced" || (["complete", "delivered"].includes(j.status) && j.invoiced === true);
    const cancelled = (j) => j.status === "cancelled";
    if (statusFilter !== "all" || !filtered.length) return filtered;
    const active = filtered.filter((j) => !done(j) && !cancelled(j));
    const groups = [
      { key: "completed", label: "Completed", rows: filtered.filter(done) },
      { key: "cancelled", label: "Cancelled", rows: filtered.filter(cancelled) },
    ];
    const out = active.length ? [...active] : [{ __empty: true, key: "no-active" }];
    groups.forEach((g) => {
      if (!g.rows.length) return;
      const open = !!openGroups[g.key];
      out.push({ __header: true, key: g.key, label: g.label, count: g.rows.length, open,
        toggle: () => setOpenGroups((o) => ({ ...o, [g.key]: !o[g.key] })) });
      if (open) out.push(...g.rows);
    });
    return out;
  }, [filtered, openGroups, statusFilter]);

  const toggleInvoiced = async (j) => {
    try {
      const next = !j.invoiced;
      await api.put(`/jobs/${j.id}`, { ...j, invoiced: next });
      setItems((prev) => prev.map((x) => x.id === j.id ? { ...x, invoiced: next } : x));
      toast.success(next ? `${j.number} marked as invoiced` : `${j.number} marked not invoiced`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Inline status change from the list — no need to open the job.
  // Special-case: switching to "complete" opens a confirmation dialog first so
  // ops can flag any late subs / drivers and log deductions in one flow.
  const [completeFor, setCompleteFor] = useState(null);
  const commitStatus = async (j, status) => {
    await api.put(`/jobs/${j.id}`, { ...j, status });
    setItems((prev) => prev.map((x) => x.id === j.id ? { ...x, status } : x));
    toast.success(`${j.number} → ${STATUS_LABEL[status] || status}`);
  };
  const updateStatus = async (j, status) => {
    if (!status || status === j.status) return;
    if (status === "complete") {
      setCompleteFor(j);
      return;
    }
    try { await commitStatus(j, status); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const saveCode = async (j, code) => {
    const next = code.trim();
    if (next === (j.code || "")) return;
    try {
      await api.put(`/jobs/${j.id}`, { ...j, code: next });
      setItems((prev) => prev.map((x) => x.id === j.id ? { ...x, code: next } : x));
      toast.success(`${j.number} code saved`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelete = async (j) => {
    if (!window.confirm(`Delete ${j.number}?`)) return;
    try { await api.delete(`/jobs/${j.id}`); toast.success(`${j.number} deleted`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="jobs-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Operations</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-1">Jobs</h1>
          <p className="text-slate-600 mt-2 text-sm">
            Dispatch board — collection, drop-off, driver, status and charge for every delivery.
          </p>
        </div>
        <Link to="/jobs/new">
          <Button data-testid="job-new-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
            <Plus className="h-4 w-4 mr-2" /> New Job
          </Button>
        </Link>
      </div>

      <BookingSheetCard kind="sub" />
      <BookingSheetCard kind="self" />
      <ContactStatementCard />
      <Card className="rounded-sm border border-slate-200 shadow-none">
        <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-64 max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="jobs-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='Search job #, customer, driver, PO, "invoiced" / "not invoiced"…'
              className="pl-9 rounded-sm border-slate-300"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="jobs-status-filter" className="w-44 rounded-sm border-slate-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="ml-auto text-xs text-slate-500">
            <span className="text-emerald-700 font-semibold">{invoicedCount} invoiced</span>
            <span className="mx-2 text-slate-300">·</span>
            <span className="text-slate-700 font-semibold">{filtered.length - invoicedCount} not invoiced</span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full po-table text-sm table-fixed">
            <colgroup>
              <col className="w-[76px]" />        {/* Job # */}
              <col className="w-[92px]" />        {/* Date */}
              <col className="w-[130px]" />       {/* Client / Customer */}
              <col />                             {/* Route — flexible */}
              <col className="w-[110px]" />       {/* Code */}
              <col className="w-[70px]" />        {/* Driver */}
              <col className="w-[84px]" />        {/* Priority */}
              <col className="w-[120px]" />       {/* Status */}
              <col className="w-[78px]" />        {/* Charge */}
              <col className="w-[120px]" />       {/* Invoiced */}
              <col className="w-[150px]" />       {/* Actions */}
            </colgroup>
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider">
                <th className="px-2.5 py-3 font-semibold">Job #</th>
                <th className="px-2.5 py-3 font-semibold">Date</th>
                <th className="px-2.5 py-3 font-semibold">Client / Customer</th>
                <th className="px-2.5 py-3 font-semibold">Route</th>
                <th className="px-2.5 py-3 font-semibold">Code</th>
                <th className="px-2.5 py-3 font-semibold">Driver</th>
                <th className="px-2.5 py-3 font-semibold">Priority</th>
                <th className="px-2.5 py-3 font-semibold">Status</th>
                <th className="px-2.5 py-3 font-semibold text-right">Charge</th>
                <th className="px-2.5 py-3 font-semibold text-center">Invoiced</th>
                <th className="px-2.5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="text-slate-900">
              {loading && (
                <tr><td colSpan="11" className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan="11" className="px-4 py-12 text-center">
                    <div className="text-slate-700 font-medium">No jobs yet</div>
                    <div className="text-slate-500 text-sm mt-1">Click &quot;New Job&quot; to add your first dispatch.</div>
                  </td>
                </tr>
              )}
              {!loading && visibleRows.map((j) => {
                if (j.__empty) return (
                  <tr key={j.key}><td colSpan="11" className="px-4 py-6 text-center text-sm text-slate-500" data-testid="jobs-no-active">No active jobs — completed &amp; invoiced and cancelled jobs are below.</td></tr>
                );
                if (j.__header) return (
                  <tr key={j.key} onClick={j.toggle} data-testid={`jobs-group-${j.key}`}
                    className="border-t border-slate-200 bg-slate-50 cursor-pointer select-none hover:bg-slate-100">
                    <td colSpan="11" className="px-3 py-3 text-xs uppercase tracking-wider font-semibold text-slate-600">
                      <span className="inline-flex items-center gap-2">
                        <ChevronRight className={`h-4 w-4 transition-transform ${j.open ? "rotate-90" : ""}`} />
                        {j.label} ({j.count})
                      </span>
                    </td>
                  </tr>
                );
                return (
                  <tr key={j.id} className={`border-t border-slate-100 hover:bg-slate-50 ${j.invoiced ? "bg-emerald-50/40" : ""}`} data-testid={`job-row-${j.id}`}>
                    <td className="px-2.5 py-3 font-medium">
                      <Link to={`/jobs/${j.id}`} className="hover:underline">{j.number}</Link>
                    </td>
                    <td className="px-2.5 py-3 text-slate-700 whitespace-nowrap">{fmtDate(j.date)}</td>
                    <td className="px-2.5 py-3 text-slate-700 break-words">{j.customer_snapshot?.name || "—"}</td>
                    <td className="px-2.5 py-3 text-slate-700">
                      <div className="flex items-center gap-1 text-xs min-w-0">
                        <MapPin className="h-3 w-3 text-slate-400 shrink-0" />
                        <span className="truncate" title={j.collection_address}>{j.collection_address || "—"}</span>
                        <ArrowRight className="h-3 w-3 text-slate-400 shrink-0" />
                        <span className="truncate" title={j.dropoff_address}>{j.dropoff_address || "—"}</span>
                      </div>
                    </td>
                    <td className="px-2.5 py-3">
                      <Input
                        key={`${j.id}-${j.code || ""}`}
                        data-testid={`job-code-${j.id}`}
                        defaultValue={j.code || ""}
                        placeholder="Add code"
                        onBlur={(e) => saveCode(j, e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                        className="h-7 rounded-sm border-slate-200 text-xs px-2"
                      />
                    </td>
                    <td className="px-2.5 py-3 text-slate-700 break-words">{j.driver_snapshot?.name || j.assignee_other || "—"}</td>
                    <td className="px-2.5 py-3">
                      <span className={`inline-block text-[11px] uppercase tracking-wider px-2 py-1 rounded-sm border ${PRIORITY_STYLES[j.priority] || PRIORITY_STYLES.normal}`}>
                        {j.priority || "normal"}
                      </span>
                    </td>
                    <td className="px-2.5 py-3">
                      <Select
                        value={j.status || "new"}
                        onValueChange={(v) => updateStatus(j, v)}
                      >
                        <SelectTrigger
                          data-testid={`job-status-${j.id}`}
                          className={`h-7 w-full rounded-sm text-[11px] uppercase tracking-wider font-semibold border ${STATUS_STYLES[j.status] || STATUS_STYLES.new}`}
                        >
                          <SelectValue placeholder={STATUS_LABEL[j.status] || j.status} />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.filter((o) => o.value !== "all").map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-2.5 py-3 text-right tabular-nums">{gbp(j.charge || 0)}</td>
                    <td className="px-2.5 py-3 text-center">
                      <div className="inline-flex items-center gap-1.5">
                        <Switch
                          data-testid={`job-invoiced-${j.id}`}
                          checked={!!j.invoiced}
                          onCheckedChange={() => toggleInvoiced(j)}
                          className="data-[state=checked]:bg-emerald-600"
                        />
                        <span className={`text-[10px] uppercase tracking-wider ${j.invoiced ? "text-emerald-700 font-semibold" : "text-slate-500"}`}>
                          {j.invoiced ? "Yes" : "No"}
                        </span>
                      </div>
                    </td>
                    <td className="px-2.5 py-3 text-right">
                      <div className="inline-flex gap-1 items-center">
                        <a
                          href={fileUrl(`/jobs/${j.id}/pdf`)}
                          download
                          rel="noopener"
                          data-testid={`job-pdf-${j.id}`}
                          title="Download internal Job Sheet PDF"
                          className="inline-flex items-center justify-center h-8 w-8 rounded-sm text-slate-600 hover:bg-slate-100"
                        >
                          <FileDown className="h-4 w-4" />
                        </a>
                        <a
                          href={fileUrl(`/jobs/${j.id}/client-pdf`)}
                          download
                          rel="noopener"
                          data-testid={`job-client-pdf-${j.id}`}
                          title="Download client-ready Delivery Note (no supplier info or pricing)"
                          className="inline-flex items-center justify-center h-8 w-8 rounded-sm text-emerald-700 hover:bg-emerald-50"
                        >
                          <Send className="h-4 w-4" />
                        </a>
                        <Link to={`/jobs/${j.id}`}>
                          <Button data-testid={`job-edit-${j.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm">
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </Link>
                        <RepeatJobDialog
                          jobId={j.id}
                          defaultStart={j.date}
                          size="sm"
                          variant="ghost"
                          buttonClassName="h-8 w-8 rounded-sm text-slate-600 hover:bg-slate-100 p-0 inline-flex items-center justify-center"
                          triggerTestId={`job-repeat-${j.id}`}
                          triggerLabel=""
                          triggerIcon={<Copy className="h-4 w-4" />}
                          triggerTitle="Repeat this job on more dates"
                          showIcon={false}
                          onCreated={() => load()}
                        />
                        <Button data-testid={`job-delete-${j.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-600 hover:text-red-700" onClick={() => onDelete(j)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <CompleteJobDialog
        open={!!completeFor}
        onOpenChange={(v) => { if (!v) setCompleteFor(null); }}
        job={completeFor}
        onCompleted={async () => {
          if (!completeFor) return;
          try { await commitStatus(completeFor, "complete"); }
          catch (e) { toast.error(formatApiError(e)); throw e; }
        }}
      />
    </div>
  );
}

function todayISO() { return new Date().toISOString().slice(0, 10); }

function BookingSheetCard({ kind = "sub" }) {
  const isSelf = kind === "self";
  const wantType = isSelf ? "subcontractor" : "customer"; // contacts.type naming is legacy
  const title = isSelf ? "Self-employed driver booking sheet" : "Subcontractor booking sheet";
  const personLabel = isSelf ? "Self-employed driver" : "Subcontractor";
  const description = isSelf
    ? "Pick a self-employed driver and a date range — download a clean PDF with the sites, services and driver counts they're covering, then forward it to them."
    : "Pick a subcontractor and a date range — download a clean PDF with the sites, services and driver counts they're covering, then forward it to them.";

  const [people, setPeople] = useState([]);
  const [personId, setPersonId] = useState("");
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("__all");
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());

  useEffect(() => {
    api.get("/contacts").then((r) => {
      const list = (r.data || []).filter((c) => c.type === wantType);
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setPeople(list);
    }).catch(() => setPeople([]));
    api.get("/clients").then((r) => {
      const list = [...(r.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setClients(list);
    }).catch(() => setClients([]));
  }, [wantType]);

  const [open, setOpen] = useState(false);   // collapsed by default
  const ready = personId && dateFrom && dateTo && dateTo >= dateFrom;
  const clientParam = clientId && clientId !== "__all" ? `&client_id=${encodeURIComponent(clientId)}` : "";
  const url = ready
    ? fileUrl(`/jobs/booking-sheet.pdf?subcontractor_id=${encodeURIComponent(personId)}&date_from=${dateFrom}&date_to=${dateTo}${clientParam}`)
    : "#";

  return (
    <Card className="rounded-sm border border-slate-200 shadow-none p-5" data-testid={`booking-sheet-card-${kind}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`booking-${kind}-toggle`}
        className="w-full flex items-start gap-3 text-left group"
      >
        <div className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-slate-900 text-white shrink-0">
          <FileDown className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-900">{title}</div>
          <div className="text-xs text-slate-500 mt-0.5">{description}</div>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-slate-400 shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
      <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div>
          <label className="text-xs text-slate-600">{personLabel}</label>
          <Select value={personId || undefined} onValueChange={setPersonId}>
            <SelectTrigger data-testid={`booking-${kind}-person`} className="mt-1 rounded-sm border-slate-300"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {people.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.email || s.id}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-slate-600">Client</label>
          <BookingClientCombobox
            value={clientId}
            onChange={setClientId}
            clients={clients}
            testid={`booking-${kind}-client`}
          />
        </div>
        <div>
          <label className="text-xs text-slate-600">From</label>
          <Input data-testid={`booking-${kind}-from`} type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
        </div>
        <div>
          <label className="text-xs text-slate-600">To</label>
          <Input data-testid={`booking-${kind}-to`} type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
        </div>
        <div>
          <Button asChild={!!ready} disabled={!ready} data-testid={`booking-${kind}-download`} className={`w-full rounded-sm ${ready ? "bg-slate-900 hover:bg-slate-800 text-white" : "bg-slate-200 text-slate-400 cursor-not-allowed"}`}>
            {ready ? (
              <a href={url} download rel="noopener">
                <FileDown className="w-4 h-4 mr-1.5" /> Download PDF
              </a>
            ) : (
              <span><FileDown className="w-4 h-4 mr-1.5 inline" /> Pick one first</span>
            )}
          </Button>
        </div>
      </div>
      )}
    </Card>
  );
}

// Searchable client picker for the booking-sheet card. "__all" = every client.
function BookingClientCombobox({ value, onChange, clients, testid }) {
  const [open, setOpen] = useState(false);
  const isAll = !value || value === "__all";
  const selected = clients.find((c) => c.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className="mt-1 flex h-9 w-full items-center justify-between rounded-sm border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
        >
          <span className="truncate text-left">
            {isAll ? "All clients" : (selected?.name || "Choose client")}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-slate-400 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0 rounded-sm" align="start">
        <Command>
          <CommandInput placeholder="Type to search clients..." className="h-9" />
          <CommandList>
            <CommandEmpty>No clients match.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="all-clients"
                onSelect={() => { onChange("__all"); setOpen(false); }}
                data-testid={`${testid}-all`}
              >
                <Check className={`mr-2 h-4 w-4 ${isAll ? "opacity-100" : "opacity-0"}`} />
                <span className="font-medium">All clients</span>
              </CommandItem>
              {clients.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.name}
                  onSelect={() => { onChange(c.id); setOpen(false); }}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === c.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{c.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// One-click month-end statement for a subcontractor or self-employed driver —
// combines every job they covered (with per-row pay) and every deduction taken
// from them in the picked window, netting to what we owe.
function ContactStatementCard() {
  const [people, setPeople] = useState([]);
  const [personId, setPersonId] = useState("");
  const [dateFrom, setDateFrom] = useState(monthStartISO());
  const [dateTo, setDateTo] = useState(todayISO());

  useEffect(() => {
    api.get("/contacts").then((r) => {
      const list = (r.data || []).filter((c) => c.type === "subcontractor" || c.type === "self_employed");
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setPeople(list);
    }).catch(() => setPeople([]));
  }, []);

  const ready = personId && dateFrom && dateTo && dateTo >= dateFrom;
  const [open, setOpen] = useState(false);   // collapsed by default
  const url = ready
    ? fileUrl(`/contacts/${encodeURIComponent(personId)}/statement.pdf?date_from=${dateFrom}&date_to=${dateTo}`)
    : "#";

  return (
    <Card className="rounded-sm border border-slate-200 shadow-none p-5" data-testid="statement-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="statement-toggle"
        className="w-full flex items-start gap-3 text-left group"
      >
        <div className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-slate-900 text-white shrink-0">
          <Receipt className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-900">Contact statement</div>
          <div className="text-xs text-slate-500 mt-0.5">
            One-page monthly statement — jobs covered plus deductions, netted to what you owe. Pick any subcontractor or self-employed driver.
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
      <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div>
          <label className="text-xs text-slate-600">Subcontractor / self-employed</label>
          <Select value={personId || undefined} onValueChange={setPersonId}>
            <SelectTrigger data-testid="statement-person" className="mt-1 rounded-sm border-slate-300">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {people.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} <span className="text-slate-400 text-[10px] ml-1">· {p.type === "self_employed" ? "Self-emp" : "Sub"}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-slate-600">From</label>
          <Input data-testid="statement-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                 className="mt-1 rounded-sm border-slate-300" />
        </div>
        <div>
          <label className="text-xs text-slate-600">To</label>
          <Input data-testid="statement-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                 className="mt-1 rounded-sm border-slate-300" />
        </div>
        <div>
          <Button asChild={!!ready} disabled={!ready} data-testid="statement-download"
                  className={`w-full rounded-sm ${ready ? "bg-slate-900 hover:bg-slate-800 text-white" : "bg-slate-200 text-slate-400 cursor-not-allowed"}`}>
            {ready ? (
              <a href={url} download rel="noopener">
                <Receipt className="w-4 h-4 mr-1.5" /> Download statement
              </a>
            ) : (
              <span><Receipt className="w-4 h-4 mr-1.5 inline" /> Pick one first</span>
            )}
          </Button>
        </div>
      </div>
      )}
    </Card>
  );
}

function monthStartISO() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

